import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "cliphub-theme";

function safeTheme(value) {
  return value === "light" || value === "dark" ? value : "dark";
}

function applyThemeToDocument(nextTheme) {
  if (typeof window === "undefined") return;
  const normalized = safeTheme(nextTheme);
  window.localStorage.setItem(STORAGE_KEY, normalized);
  document.documentElement.classList.toggle("dark", normalized === "dark");
  document.documentElement.setAttribute("data-theme", normalized);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const initial = safeTheme(saved);
    applyThemeToDocument(initial);
    return initial;
  });

  const updateTheme = useCallback((next) => {
    setTheme((previous) => {
      const resolved = safeTheme(typeof next === "function" ? next(previous) : next);
      applyThemeToDocument(resolved);
      return resolved;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    updateTheme((prev) => (prev === "dark" ? "light" : "dark"));
  }, [updateTheme]);

  useLayoutEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      const next = safeTheme(event.newValue);
      setTheme(next);
      applyThemeToDocument(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    // Clean up any stale color-scheme attr left from earlier accent picker
    document.documentElement.removeAttribute("data-color-scheme");
    try { window.localStorage.removeItem("cliphub-color-scheme"); } catch {}
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme: updateTheme,
      toggleTheme,
      isDark: theme === "dark",
    }),
    [theme, updateTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
